import { useRef, useState } from "react";
import { BadgeCheck, Phone, ShieldCheck } from "lucide-react";

import TurnstileWidget from "@/components/TurnstileWidget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";

import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "/api/v1";

const EMPLOYMENT_TYPE_MAP = {
  SALARIED: "SALARIED",
  SELF_EMPLOYED: "SELF_EMPLOYED",
  PROFESSIONAL: "PROFESSIONAL",
};

export default function LeadFormSection({
  badge,
  title,
  description,
  highlights = [],
  phone,
  form,
  loanType,
}) {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [employment, setEmployment] = useState("");
  const [income, setIncome] = useState("");
  const [loanAmount, setLoanAmount] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);

  const submit = async (event) => {
    event.preventDefault();

    if (!loanType) {
      toast.error("Submission failed", {
        description: "Loan type is not configured for this page.",
      });
      return;
    }

    const trimmedName = name.trim();
    const trimmedMobile = mobile.trim();
    const trimmedLoanAmount = loanAmount.trim();

    if (trimmedName.length < 2) {
      toast.error("Please enter your full name.");
      return;
    }

    if (!/^\d{10}$/.test(trimmedMobile)) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return;
    }

    if (!employment) {
      toast.error("Please select your employment type.");
      return;
    }

    if (!income) {
      toast.error(`Please select your income/turnover.`);
      return;
    }

    if (!trimmedLoanAmount) {
      toast.error("Please enter your required loan amount.");
      return;
    }

    if (!turnstileToken) {
      toast.error("Please complete the security verification.");
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        fullName: trimmedName,
        phone: trimmedMobile,

        loanType,

        loanAmount: trimmedLoanAmount,

        employmentType: EMPLOYMENT_TYPE_MAP[employment] || employment,

        ...(form?.incomeField === "annualTurnover"
          ? { annualTurnover: income }
          : { monthlyIncome: income }),

        status: "NEW",
        priority: "MEDIUM",
        source: "WEBSITE",

        assignedTo: null,
        followUpDate: null,

        turnstileToken,
        turnstileAction: "loan-lead",
      };

      await axios.post(`${API}/leads`, payload);

      setSubmitted(true);

      toast.success("Enquiry submitted", {
        description: "A Money Mart advisor will reach out shortly.",
      });
    } catch (error) {
      turnstileRef.current?.reset();
      const response = error?.response?.data;

      if (response?.errors?.length) {
        const message = response.errors
          .map((item) => item.message)
          .filter(Boolean)
          .join(", ");

        toast.error("Submission failed", {
          description: message,
        });
      } else {
        toast.error("Submission failed", {
          description:
            response?.message || "Something went wrong. Please try again.",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="lead-form"
      className="w-full overflow-hidden bg-slate-50 py-16 sm:py-20 lg:py-24"
    >
      <div className="container mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid min-w-0 items-center gap-10 lg:grid-cols-2 lg:gap-16">
          {/* LEFT */}

          <div className="min-w-0">
            <span className="inline-flex rounded-full bg-primary/10 px-4 py-2 text-sm font-semibold text-primary">
              {badge}
            </span>

            <h2 className="mt-6 text-3xl font-bold leading-tight text-slate-900 sm:text-4xl">
              {title}
            </h2>

            <p className="mt-5 text-base leading-7 text-slate-600 sm:mt-6 sm:text-lg sm:leading-8">
              {description}
            </p>

            <div className="mt-8 space-y-5 sm:mt-10">
              {highlights.map((item) => (
                <div key={item} className="flex min-w-0 items-center gap-3 sm:gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                    <BadgeCheck className="h-5 w-5 text-primary" />
                  </div>

                  <span className="min-w-0 font-medium text-slate-700">{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 rounded-2xl border bg-white p-4 shadow-sm sm:mt-12 sm:p-6">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary text-white sm:h-14 sm:w-14">
                  <Phone className="h-6 w-6 sm:h-7 sm:w-7" />
                </div>

                <div className="min-w-0">
                  <p className="text-sm text-slate-500">{phone.label}</p>

                  <a
                    href={phone.href}
                    className="text-lg font-bold text-slate-900 hover:text-primary sm:text-xl"
                  >
                    {phone.text}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT */}

          <div className="min-w-0 rounded-3xl border bg-white p-4 shadow-xl sm:p-6 lg:p-8">
            {submitted ? (
              <div className="py-12 text-center">
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                  <BadgeCheck className="h-8 w-8 text-green-600" />
                </div>

                <h3 className="text-2xl font-bold text-slate-900">
                  Thank you!
                </h3>

                <p className="mt-3 text-slate-600">
                  Your enquiry has been submitted successfully. A Money Mart
                  advisor will contact you shortly.
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-bold">{form.title}</h3>

                <p className="mt-2 text-slate-600">{form.description}</p>

                <form className="mt-8 space-y-5" onSubmit={submit}>
                  <Input
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder={form.placeholders.name}
                    autoComplete="name"
                  />

                  <Input
                    value={mobile}
                    onChange={(event) =>
                      setMobile(
                        event.target.value.replace(/\D/g, "").slice(0, 10),
                      )
                    }
                    placeholder={form.placeholders.mobile}
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                  />

                  <Select value={employment} onValueChange={setEmployment}>
                    <SelectTrigger>
                      <SelectValue placeholder={form.placeholders.employment} />
                    </SelectTrigger>

                    <SelectContent>
                      {form.employmentOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={income} onValueChange={setIncome}>
                    <SelectTrigger>
                      <SelectValue placeholder={form.placeholders.income} />
                    </SelectTrigger>

                    <SelectContent>
                      {form.incomeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Input
                    value={loanAmount}
                    onChange={(event) => setLoanAmount(event.target.value)}
                    placeholder={form.placeholders.loanAmount}
                    inputMode="numeric"
                  />

                  <div className="w-full min-w-0 overflow-hidden">
                    <TurnstileWidget
                      ref={turnstileRef}
                      action="loan-lead"
                      onTokenChange={setTurnstileToken}
                      className="w-full max-w-full"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting || !turnstileToken}
                    className="w-full h-12 text-base"
                    size="lg"
                  >
                    {submitting ? "Submitting..." : form.submitButton}
                  </Button>
                </form>

                <div className="mt-6 flex min-w-0 items-start gap-3 text-sm text-slate-500">
                  <ShieldCheck className="h-5 w-5 shrink-0 text-primary" />

                  <p className="min-w-0 break-words">{form.privacyText}</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
